import type { AppContext, AppProps } from 'next/app';
import { SWRConfig } from 'swr';
import ThemeProvider from '@/components/ThemeProvider';
import GlobalStyles from '@/styles/GlobalStyles';
import { ColorModeProvider } from '@/contexts/ColorModeContext';
import InlineScript from '@/styles/InlineScript';
import '@/public/css/lexical_editor.scss';
import '@/public/css/DateRangePicker.scss';

import '@liveblocks/react-ui/styles.css';
import '@liveblocks/react-ui/styles/dark/media-query.css';
import '@liveblocks/react-lexical/styles.css';
import '@/styles/react-datepicker.css';
import 'swiper/css'; // Swiper 기본 스타일 (App Box 탭 등 전역 Swiper 레이아웃)
import 'reactflow/dist/style.css';
import { Provider } from 'jotai';
import SeoHeader from '@/components/SeoHeader';
import LoadingLottie from '@/components/LoadingLottie';
import { useEffect, useRef, useState } from 'react';
import useHotjar from 'react-use-hotjar';
import { useRouter } from 'next/router';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/public/utils/i18n';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/ko';
import dayjs from 'dayjs';
import { ModalPortalId } from '@/components/@IntranetLibrary/Modal';
import { isProd } from '@/constant';
import { InitializeGTM } from '@/components/GTM/InitializeGTM';
import LanguageSync from '@/components/LanguageSync';
import { GlobalToolsProvider } from '@/components/@IntranetLibrary/GlobalToolsProvider';
import { RecorderProvider } from '@/components/RecorderProvider';
import FileUploadStatusModal from '@/components/Common/FileUploadStatusModal';
import { PAGE_URL } from '@/constant/url';
import { AuthProvider } from '@/contexts/AuthContext';
import { RNAuthInfo } from '@/types/api';
import { hydrateTokenFromQuery, setCookie } from '@/utils/tokens';
import FileViewerContainer from '@/components/FileViewer/FileViewerContainer';
import {
  COOKIE_KEY_ACCESS_TOKEN,
  COOKIE_KEY_MS_ACCESS_TOKEN,
  COOKIE_KEY_MS_REFRESH_TOKEN,
  COOKIE_KEY_REFRESH_TOKEN,
} from '@/constant/keys';
import { resolveRNTokenPromise } from '@/utils/function';
import { installDomTranslateGuard } from '@/utils/domTranslateGuard';
import ComponentStackBoundary from '@/components/AppErrorPage/ComponentStackBoundary';
import {
  AppleGothicNeoSD,
  Arizonia,
  Baloo2,
  NotoSans,
  Poppins,
  Pretendard,
} from '@/styles/fonts';

dayjs.locale('ko');

const myCustomLogger = console.log;

// 브라우저 자동 번역(Chrome · Edge)이 옮긴 노드 때문에 React 가 죽지 않게 — 하이드레이션 전에 한 번 (utils/domTranslateGuard)
installDomTranslateGuard();

const AppContent = ({ Component, pageProps }: AppProps<AppContext>) => {
  const [loading, setLoading] = useState(true);
  const [opacity, setOpacity] = useState<0 | 1>(1);
  const router = useRouter();
  // effect 는 자식이 먼저 실행돼 초기화 API 가 토큰 없이 나감 — 첫 렌더 중 1회 승격
  const tokenHydratedRef = useRef(false);
  if (!tokenHydratedRef.current) {
    tokenHydratedRef.current = true;
    hydrateTokenFromQuery();
  }
  const needLoading =
    router.route === PAGE_URL.MAIN || router.route === PAGE_URL.HOME_MOBILE;

  const { initHotjar } = useHotjar();

  useEffect(() => {
    const timeout1 = setTimeout(() => setOpacity(0), 1800); // Opacity를 0으로 설정
    const timeout2 = setTimeout(() => setLoading(false), 2300); // 로딩 상태 해제

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2); // 타이머 정리
    };
  }, []);

  useEffect(() => {
    initHotjar(isProd ? 3730425 : 3854712, 6, false, myCustomLogger);
  }, [initHotjar]);

  const setRnAuthInfo = async (authInfo: RNAuthInfo) => {
    const { portalApiKey, refreshToken, msAccessToken, msRefreshToken } =
      authInfo;
    setCookie(COOKIE_KEY_ACCESS_TOKEN, portalApiKey);
    setCookie(COOKIE_KEY_REFRESH_TOKEN, refreshToken);
    setCookie(COOKIE_KEY_MS_ACCESS_TOKEN, msAccessToken);
    setCookie(COOKIE_KEY_MS_REFRESH_TOKEN, msRefreshToken);
  };

  const handleMessageEvent = async (event: MessageEvent | Event) => {
    if ('data' in event && typeof event.data === 'string' && event.data) {
      try {
        const parsed = JSON.parse(event?.data);
        switch (parsed.type) {
          case 'TOKEN_INFO': {
            const authInfo = parsed.data as RNAuthInfo;
            await setRnAuthInfo(authInfo);
            // Promise resolve - 토큰 설정 완료 신호
            resolveRNTokenPromise();
            break;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (window.isRNWebview2) {
      window.addEventListener('message', handleMessageEvent);
      document.addEventListener('message', (e) => {
        handleMessageEvent(e);
      });
    }
    return () => {
      window.removeEventListener('message', handleMessageEvent);
      document.removeEventListener('message', (e) => handleMessageEvent(e));
    };
  }, []);

  return (
    <div
      className={[
        AppleGothicNeoSD.variable,
        Arizonia.variable,
        Baloo2.variable,
        NotoSans.variable,
        Poppins.variable,
        Pretendard.variable,
      ].join(' ')}
    >
      <SeoHeader />
      <AuthProvider>
        <SWRConfig>
          <ColorModeProvider>
            <InlineScript />
            <ThemeProvider>
              <GlobalStyles />
              <I18nextProvider i18n={i18n}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="ko"
                >
                  {/*<LocalizationProvider dateAdapter={AdapterDateFns}>*/}
                  <Provider>
                    {/* GTM 초기화 - Provider 하위에 있어야 함 */}
                    <InitializeGTM />
                    {/* 라우트별 언어 설정 적용 - MyInfoState 를 읽으므로 Provider 하위 */}
                    <LanguageSync />
                    <GlobalToolsProvider>
                      {/* TODO RecorderProvider 도  GlobalToolsProvider 로 합치기 @bside-JM */}
                      <RecorderProvider>
                        {needLoading && loading && (
                          <LoadingLottie opacity={opacity} />
                        )}
                        <Component {...pageProps} />
                        <div id={ModalPortalId} />
                        <FileUploadStatusModal />
                        <FileViewerContainer />
                      </RecorderProvider>
                    </GlobalToolsProvider>
                  </Provider>
                </LocalizationProvider>
              </I18nextProvider>
            </ThemeProvider>
          </ColorModeProvider>
        </SWRConfig>
      </AuthProvider>
    </div>
  );
};

// App 자체의 훅·Provider 에서 난 에러까지 컴포넌트 스택을 남기도록 가장 바깥에서 감싼다
const App = (props: AppProps<AppContext>) => (
  <ComponentStackBoundary>
    <AppContent {...props} />
  </ComponentStackBoundary>
);

export default App;
